import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useCreateUserMutation } from "@/features/api/authApi";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";

const CreateUser = () => {
  const navigate = useNavigate();
  const [createUser, { isLoading }] = useCreateUserMutation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "student",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const validateForm = (formData) => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return false;
    }
    if (!formData.email.includes("@") || !formData.email.includes(".")) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!formData.phone.trim() || !/^\+?\d{10,15}$/.test(formData.phone.replace(/\D/g, ""))) {
      toast.error("Please enter a valid phone number (10-15 digits)");
      return false;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    if (!["user", "student", "customer", "instructor", "admin"].includes(formData.role)) {
      toast.error("Please select a valid role");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form inputs
    if (!validateForm(form)) return;

    // Prepare payload
    const userData = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      role: form.role,
      password: form.password,
    };

    // Optional: Transform payload if API expects different field names (e.g., 'phone' instead of 'phone')
    // const userData = { ...form, phone: form.phone, phone: undefined };

    console.log("Submitting create user payload:", userData);

    try {
      const response = await createUser(userData).unwrap();
      toast.success(response.message || "User created successfully");
      navigate("/admin/users");
    } catch (err) {
      console.error("User creation failed:", {
        status: err.status,
        data: err.data,
        message: err.data?.message || "Unknown error",
      });
      toast.error(err?.data?.message || "Failed to create user. Please try again.");
    }
  };

  return (
    <div className="flex-1 p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">👤 Create User</h1>
        <Button
          variant="outline"
          onClick={() => navigate("/admin/users")}
          className="hover:bg-blue-600 hover:text-white"
          disabled={isLoading}
        >
          Back to Users →
        </Button>
      </div>
      <Card className="p-4 max-w-2xl mx-auto mt-10">
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter user name"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter user email"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  required
                  minLength={6}
                  disabled={isLoading}
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number (e.g., +911234567890)"
                  disabled={isLoading}
                />
              </div>

              <div>
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring focus:ring-blue-500"
                  disabled={isLoading}
                >
                  <option value="user">User</option>
                  <option value="student">Student</option>
                  <option value="customer">Customer</option>
                  <option value="instructor">Instructor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/admin/users")}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create User
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateUser;